import { type CSSResult, unsafeCSS } from 'lit';

import token from '../utils/get-token';

export type TypographyVariant =
	| 'body-lg-bold'
	| 'body-lg'
	| 'body-md-bold-underline'
	| 'body-md-bold'
	| 'body-md-underline'
	| 'body-md'
	| 'body-sm-bold-underline'
	| 'body-sm-bold'
	| 'body-sm-underline'
	| 'body-sm'
	| 'button-lg'
	| 'button-md'
	| 'button-sm'
	| 'chips-md'
	| 'chips-sm'
	| 'heading-lg-bold'
	| 'heading-lg'
	| 'heading-md-bold'
	| 'heading-md'
	| 'heading-sm-bold'
	| 'heading-sm'
	| 'hero-lg-bold'
	| 'hero-lg'
	| 'hero-md-bold'
	| 'hero-md'
	| 'hero-sm-bold'
	| 'hero-sm'
	| 'number-md'
	| 'number-sm'
	| 'table-header-md'
	| 'table-header-sm';

/**
 * Apply set of tokens for a given typography `variant`.
 * @param {TypographyVariant} [variant]
 * @returns {String}
 */
function getVariant(variant?: TypographyVariant): string {
	switch (variant) {
		case 'hero-lg-bold':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-9')};
				font-weight: ${token('font-weight-bold')}
			`;
		case 'hero-lg':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-9')};
				font-weight: ${token('font-weight-regular')}
			`;
		case 'hero-md-bold':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-8')};
				font-weight: ${token('font-weight-bold')}
			`;
		case 'hero-md':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-8')};
				font-weight: ${token('font-weight-regular')}
			`;
		case 'hero-sm-bold':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-7')};
				font-weight: ${token('font-weight-bold')}
			`;
		case 'hero-sm':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-6')};
				font-size: ${token('font-size-7')};
				font-weight: ${token('font-weight-regular')}
			`;

		case 'heading-lg-bold':
			return `
					font-family: ${token('font-family-title')};
					line-height: ${token('line-height-5')};
					font-size: ${token('font-size-6')};
					font-weight: ${token('font-weight-bold')}
				`;
		case 'heading-lg':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-6')};
				font-weight: ${token('font-weight-regular')}
			`;

		case 'heading-md-bold':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-5')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'heading-md':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-5')};
				font-weight: ${token('font-weight-regular')}
			`;

		case 'heading-sm-bold':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-4')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'heading-sm':
			return `
				font-family: ${token('font-family-title')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-4')};
				font-weight: ${token('font-weight-regular')}
			`;
		case 'body-lg-bold':
			return `
					font-family: ${token('font-family-default')};
					line-height: ${token('line-height-6')};
					font-size: ${token('font-size-5')};
					font-weight: ${token('font-weight-bold')}
				`;

		case 'body-lg':
			return `
					font-family: ${token('font-family-default')};
					line-height: ${token('line-height-6')};
					font-size: ${token('font-size-5')};
					font-weight: ${token('font-weight-regular')}
				`;

		case 'body-md':
		case 'number-md':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-regular')}
			`;

		case 'body-md-underline':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-regular')};
				text-decoration: underline
			`;

		case 'body-md-bold':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'body-md-bold-underline':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-5')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-bold')};
				text-decoration: underline
			`;

		case 'body-sm':
		case 'number-sm':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-4')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-regular')}
			`;

		case 'body-sm-underline':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-4')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-regular')};
				text-decoration: underline
			`;

		case 'body-sm-bold':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-4')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'body-sm-bold-underline':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-4')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-bold')};
				text-decoration: underline
			`;

		case 'button-lg':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-4')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'button-md':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'button-sm':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-bold')}
			`;

		case 'table-header-md':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-3')};
				font-weight: ${token('font-weight-medium')};
				text-transform: uppercase
			`;

		case 'table-header-sm':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-2')};
				font-weight: ${token('font-weight-medium')}
			`;

		case 'chips-md':
			return `
					font-family: ${token('font-family-default')};
					line-height: ${token('line-height-1')};
					font-size: ${token('font-size-2')};
					font-weight: ${token('font-weight-bold')}
				`;

		case 'chips-sm':
			return `
				font-family: ${token('font-family-default')};
				line-height: ${token('line-height-1')};
				font-size: ${token('font-size-1')};
				font-weight: ${token('font-weight-medium')};
				text-transform: uppercase
			`;

		default:
			return '';
	}
}

/**
 * Apply set of tokens for a given typography `variant`.
 * @param {TypographyVariant} [variant]
 * @returns {CSSResult}
 */
function typography(variant?: TypographyVariant): CSSResult {
	const defaultStyle = unsafeCSS(`
		font-style: normal;
		font-stretch: normal;
		letter-spacing: normal;

		font-synthesis: none;
		text-rendering: optimizeLegibility;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		-webkit-text-size-adjust: 100%;
	`);

	return unsafeCSS(`${defaultStyle}\n${getVariant(variant)}`);
}

export default typography;
